/**
 * OFBiz Normalizers (ERP/Accounting)
 *
 * DRR-027: Normalizers for ERP resources from the ofbiz subgraph
 *
 * STUB: Expand when OFBiz types are available in generated/graphql.ts
 *
 * These normalizers will convert OFBiz resources (Invoice, Order)
 * into NormalizedEntity<T> with SOX-compliant safety levels.
 */

import { SafetyLevel } from '@/types/graphql-compat';

import type {
  NormalizedEntity,
  ComplianceFramework,
} from '@/types/normalized-entity';

import { UnifiedStatus } from '@/types/normalized-entity';

// ============================================================================
// PLACEHOLDER TYPES
// These will be replaced by imports from @/generated/graphql when available
// ============================================================================

/**
 * Invoice status
 */
export type InvoiceStatus =
  | 'DRAFT'
  | 'PENDING'
  | 'SENT'
  | 'PARTIALLY_PAID'
  | 'PAID'
  | 'OVERDUE'
  | 'CANCELLED'
  | 'VOIDED';

/**
 * Invoice type
 */
export type InvoiceType = 'SALES' | 'PURCHASE' | 'CREDIT_MEMO' | 'DEBIT_MEMO';

/**
 * Placeholder Invoice type - will be replaced by ofbiz subgraph type
 */
export interface Invoice {
  __typename: 'Invoice';
  id: string;
  tenantId: string;
  invoiceNumber: string;
  invoiceType: InvoiceType;
  status: InvoiceStatus;
  customerId?: string;
  customerName?: string;
  vendorId?: string;
  vendorName?: string;
  invoiceDate: string;
  dueDate: string;
  subtotal: number;
  taxAmount: number;
  totalAmount: number;
  paidAmount: number;
  balanceDue: number;
  currency: string;
  description?: string;
  lineItems?: InvoiceLineItem[];
  createdAt: string;
  updatedAt: string;
}

/**
 * Invoice line item
 */
export interface InvoiceLineItem {
  id: string;
  productId?: string;
  productName: string;
  quantity: number;
  unitPrice: number;
  taxRate: number;
  amount: number;
}

/**
 * Order status
 */
export type OrderStatus =
  | 'DRAFT'
  | 'PENDING'
  | 'CONFIRMED'
  | 'PROCESSING'
  | 'SHIPPED'
  | 'DELIVERED'
  | 'COMPLETED'
  | 'CANCELLED'
  | 'RETURNED';

/**
 * Order type
 */
export type OrderType = 'SALES' | 'PURCHASE' | 'INTERNAL' | 'RETURN';

/**
 * Placeholder Order type - will be replaced by ofbiz subgraph type
 */
export interface Order {
  __typename: 'Order';
  id: string;
  tenantId: string;
  orderNumber: string;
  orderType: OrderType;
  status: OrderStatus;
  customerId?: string;
  customerName?: string;
  vendorId?: string;
  vendorName?: string;
  orderDate: string;
  expectedDeliveryDate?: string;
  subtotal: number;
  taxAmount: number;
  shippingAmount: number;
  totalAmount: number;
  currency: string;
  shippingAddress?: {
    street1: string;
    street2?: string;
    city: string;
    state: string;
    postalCode: string;
    country: string;
  };
  lineItems?: OrderLineItem[];
  createdAt: string;
  updatedAt: string;
}

/**
 * Order line item
 */
export interface OrderLineItem {
  id: string;
  productId?: string;
  productName: string;
  quantity: number;
  unitPrice: number;
  taxRate: number;
  amount: number;
  status?: 'PENDING' | 'ALLOCATED' | 'SHIPPED' | 'DELIVERED' | 'CANCELLED';
}

// ============================================================================
// STATUS MAPPINGS
// ============================================================================

/**
 * Map Invoice status to UnifiedStatus
 */
const INVOICE_STATUS_MAP: Record<InvoiceStatus, UnifiedStatus> = {
  DRAFT: UnifiedStatus.PENDING,
  PENDING: UnifiedStatus.PENDING,
  SENT: UnifiedStatus.ACTIVE,
  PARTIALLY_PAID: UnifiedStatus.ACTIVE,
  PAID: UnifiedStatus.COMPLETED,
  OVERDUE: UnifiedStatus.BLOCKED,
  CANCELLED: UnifiedStatus.CANCELLED,
  VOIDED: UnifiedStatus.CANCELLED,
} as const;

/**
 * Map Order status to UnifiedStatus
 */
const ORDER_STATUS_MAP: Record<OrderStatus, UnifiedStatus> = {
  DRAFT: UnifiedStatus.PENDING,
  PENDING: UnifiedStatus.PENDING,
  CONFIRMED: UnifiedStatus.PENDING,
  PROCESSING: UnifiedStatus.ACTIVE,
  SHIPPED: UnifiedStatus.ACTIVE,
  DELIVERED: UnifiedStatus.ACTIVE,
  COMPLETED: UnifiedStatus.COMPLETED,
  CANCELLED: UnifiedStatus.CANCELLED,
  RETURNED: UnifiedStatus.FAILED,
} as const;

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Get display name for an Invoice
 */
function getInvoiceDisplayName(invoice: Invoice): string {
  const partyName = invoice.customerName || invoice.vendorName;
  if (partyName) {
    return `${invoice.invoiceNumber} - ${partyName}`;
  }
  return invoice.invoiceNumber;
}

/**
 * Get display name for an Order
 */
function getOrderDisplayName(order: Order): string {
  const partyName = order.customerName || order.vendorName;
  if (partyName) {
    return `${order.orderNumber} - ${partyName}`;
  }
  return order.orderNumber;
}

/**
 * Determine safety level based on invoice amount
 */
function getInvoiceSafetyLevel(invoice: Invoice): SafetyLevel {
  const amount = invoice.totalAmount;

  // Large invoices are critical (over $100k)
  if (amount > 100000) {
    return SafetyLevel.CRITICAL;
  }
  // Medium invoices are high priority (over $10k)
  if (amount > 10000) {
    return SafetyLevel.HIGH;
  }
  // Small invoices are medium priority
  return SafetyLevel.MEDIUM;
}

/**
 * Determine safety level based on order amount
 */
function getOrderSafetyLevel(order: Order): SafetyLevel {
  const amount = order.totalAmount;

  // Large orders are critical (over $100k)
  if (amount > 100000) {
    return SafetyLevel.CRITICAL;
  }
  // Medium orders are high priority (over $10k)
  if (amount > 10000) {
    return SafetyLevel.HIGH;
  }
  // Small orders are medium priority
  return SafetyLevel.MEDIUM;
}

/**
 * Determine if invoice requires dual approval
 */
function requiresDualApprovalForInvoice(invoice: Invoice): boolean {
  // Credit memos and large amounts require dual approval
  if (invoice.invoiceType === 'CREDIT_MEMO') {
    return true;
  }
  // Invoices over $25k require dual approval
  return invoice.totalAmount > 25000;
}

/**
 * Determine if order requires dual approval
 */
function requiresDualApprovalForOrder(order: Order): boolean {
  // Orders over $25k require dual approval
  return order.totalAmount > 25000;
}

// ============================================================================
// INVOICE NORMALIZER
// ============================================================================

/**
 * Normalize an Invoice into NormalizedEntity<Invoice>
 *
 * Invoices are financial documents requiring SOX compliance.
 *
 * @param invoice - The Invoice from ofbiz
 * @returns Fully typed NormalizedEntity with Invoice preserved in domainData
 */
export function normalizeInvoice(invoice: Invoice): NormalizedEntity<Invoice> {
  const safetyLevel = getInvoiceSafetyLevel(invoice);

  // Map status to unified status
  const unifiedStatus = INVOICE_STATUS_MAP[invoice.status] ?? UnifiedStatus.PENDING;

  // Invoice is blocked if overdue, cancelled, or voided
  const isBlockedBySafety =
    invoice.status === 'OVERDUE' ||
    invoice.status === 'CANCELLED' || // STRING-ENUM-EXCEPTION: no generated enum for this subgraph status yet
    invoice.status === 'VOIDED';

  return {
    id: invoice.id,
    displayName: getInvoiceDisplayName(invoice),
    sourceSubgraph: 'ofbiz',
    sourceType: 'Invoice',
    safetyLevel,
    requiresDualApproval: requiresDualApprovalForInvoice(invoice),
    complianceFrameworks: ['SOX'] as ComplianceFramework[],
    unifiedStatus,
    isBlockedBySafety,
    domainData: invoice,
    normalizedAt: new Date().toISOString(),
  };
}

// ============================================================================
// ORDER NORMALIZER
// ============================================================================

/**
 * Normalize an Order into NormalizedEntity<Order>
 *
 * Orders are financial commitments requiring SOX compliance.
 *
 * @param order - The Order from ofbiz
 * @returns Fully typed NormalizedEntity with Order preserved in domainData
 */
export function normalizeOrder(order: Order): NormalizedEntity<Order> {
  const safetyLevel = getOrderSafetyLevel(order);

  // Map status to unified status
  const unifiedStatus = ORDER_STATUS_MAP[order.status] ?? UnifiedStatus.PENDING;

  // Order is blocked if cancelled or returned
  const isBlockedBySafety =
    order.status === 'CANCELLED' || // STRING-ENUM-EXCEPTION: no generated enum for this subgraph status yet
    order.status === 'RETURNED'; // STRING-ENUM-EXCEPTION: no generated enum for this subgraph status yet

  return {
    id: order.id,
    displayName: getOrderDisplayName(order),
    sourceSubgraph: 'ofbiz',
    sourceType: 'Order',
    safetyLevel,
    requiresDualApproval: requiresDualApprovalForOrder(order),
    complianceFrameworks: ['SOX'] as ComplianceFramework[],
    unifiedStatus,
    isBlockedBySafety,
    domainData: order,
    normalizedAt: new Date().toISOString(),
  };
}

// ============================================================================
// TYPE GUARDS
// ============================================================================

/**
 * Check if an object is an Invoice
 */
export function isInvoice(obj: unknown): obj is Invoice {
  return (
    typeof obj === 'object' &&
    obj !== null &&
    '__typename' in obj &&
    (obj as { __typename: string }).__typename === 'Invoice'
  );
}

/**
 * Check if an object is an Order
 */
export function isOrder(obj: unknown): obj is Order {
  return (
    typeof obj === 'object' &&
    obj !== null &&
    '__typename' in obj &&
    (obj as { __typename: string }).__typename === 'Order'
  );
}
